// src/components/admin/DataManagement.jsx
// Reviewed against the supplied file: :contentReference[oaicite:0]{index=0}

import React, { useCallback, useEffect, useMemo, useState } from "react";

const DataManagement = ({ apiBase, authHeaders = {}, onUnauthorized }) => {
  const DATA_API = useMemo(() => {
    const base = String(apiBase || "").replace(/\/+$/, "");
    return `${base}/admin/data-management`;
  }, [apiBase]);

  // =====================================================
  // MAIN TAB
  // =====================================================

  const [activeTab, setActiveTab] = useState("institutes");

  // =====================================================
  // INSTITUTES
  // =====================================================

  const [institutes, setInstitutes] = useState([]);
  const [instituteName, setInstituteName] = useState("");
  const [instituteSearch, setInstituteSearch] = useState("");

  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [addingInstitute, setAddingInstitute] = useState(false);

  const [instituteMessage, setInstituteMessage] = useState({
    type: "",
    text: "",
  });

  // =====================================================
  // DISTRICTS / AREAS
  // =====================================================

  const [districts, setDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState("");

  const [areas, setAreas] = useState([]);
  const [areaName, setAreaName] = useState("");
  const [areaSearch, setAreaSearch] = useState("");

  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [addingArea, setAddingArea] = useState(false);

  const [areaMessage, setAreaMessage] = useState({
    type: "",
    text: "",
  });

  // =====================================================
  // REQUEST HELPER
  // =====================================================

  const request = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...authHeaders,
          ...(options.headers || {}),
        },
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if ([401, 403].includes(response.status)) {
        onUnauthorized?.();

        throw new Error(data?.message || "Your admin session has expired.");
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Request failed with status ${response.status}`,
        );
      }

      return data;
    },
    [authHeaders, onUnauthorized],
  );

  // =====================================================
  // LOAD INSTITUTES
  // =====================================================

  const loadInstitutes = useCallback(async () => {
    if (!DATA_API) return;

    setLoadingInstitutes(true);

    try {
      const result = await request(`${DATA_API}/institutes`);

      const rows = Array.isArray(result?.data) ? result.data : [];

      setInstitutes(rows);
    } catch (error) {
      console.error("Load institutes error:", error);

      setInstituteMessage({
        type: "error",
        text: error?.message || "Failed to load institutes.",
      });
    } finally {
      setLoadingInstitutes(false);
    }
  }, [DATA_API, request]);

  // =====================================================
  // LOAD DISTRICTS
  // =====================================================

  const loadDistricts = useCallback(async () => {
    if (!DATA_API) return;

    setLoadingDistricts(true);

    try {
      const result = await request(`${DATA_API}/districts`);

      const rows = Array.isArray(result?.data) ? result.data : [];

      setDistricts(rows);
    } catch (error) {
      console.error("Load districts error:", error);

      setAreaMessage({
        type: "error",
        text: error?.message || "Failed to load districts.",
      });
    } finally {
      setLoadingDistricts(false);
    }
  }, [DATA_API, request]);

  // =====================================================
  // LOAD AREAS
  // =====================================================

  const loadAreas = useCallback(
    async (districtId) => {
      if (!districtId) {
        setAreas([]);
        return;
      }

      setLoadingAreas(true);

      try {
        const result = await request(
          `${DATA_API}/areas?districtId=${encodeURIComponent(districtId)}`,
        );

        const rows = Array.isArray(result?.data) ? result.data : [];

        setAreas(rows);
      } catch (error) {
        console.error("Load areas error:", error);

        setAreaMessage({
          type: "error",
          text: error?.message || "Failed to load areas.",
        });
      } finally {
        setLoadingAreas(false);
      }
    },
    [DATA_API, request],
  );

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    loadInstitutes();
    loadDistricts();
  }, [loadInstitutes, loadDistricts]);

  // =====================================================
  // LOAD AREAS WHEN DISTRICT CHANGES
  // =====================================================

  useEffect(() => {
    setAreaSearch("");

    if (selectedDistrictId) {
      loadAreas(selectedDistrictId);
    } else {
      setAreas([]);
    }
  }, [selectedDistrictId, loadAreas]);

  // =====================================================
  // ADD INSTITUTE
  // =====================================================

  const handleAddInstitute = async (event) => {
    event.preventDefault();

    if (addingInstitute) return;

    const cleanName = instituteName.trim();

    setInstituteMessage({
      type: "",
      text: "",
    });

    if (!cleanName) {
      setInstituteMessage({
        type: "error",
        text: "Please enter an institute name.",
      });
      return;
    }

    if (cleanName.length > 150) {
      setInstituteMessage({
        type: "error",
        text: "Institute name cannot exceed 150 characters.",
      });
      return;
    }

    setAddingInstitute(true);

    try {
      const result = await request(`${DATA_API}/institutes`, {
        method: "POST",
        body: JSON.stringify({
          name: cleanName,
        }),
      });

      setInstituteName("");

      setInstituteMessage({
        type: "success",
        text: result?.message || "Institute added successfully.",
      });

      await loadInstitutes();
    } catch (error) {
      console.error("Add institute error:", error);

      setInstituteMessage({
        type: "error",
        text: error?.message || "Failed to add institute.",
      });
    } finally {
      setAddingInstitute(false);
    }
  };

  // =====================================================
  // ADD AREA
  // =====================================================

  const handleAddArea = async (event) => {
    event.preventDefault();

    if (addingArea) return;

    const cleanName = areaName.trim();
    const districtId = Number(selectedDistrictId);

    setAreaMessage({
      type: "",
      text: "",
    });

    if (!Number.isInteger(districtId) || districtId <= 0) {
      setAreaMessage({
        type: "error",
        text: "Please select a district.",
      });
      return;
    }

    if (!cleanName) {
      setAreaMessage({
        type: "error",
        text: "Please enter an area name.",
      });
      return;
    }

    if (cleanName.length > 100) {
      setAreaMessage({
        type: "error",
        text: "Area name cannot exceed 100 characters.",
      });
      return;
    }

    setAddingArea(true);

    try {
      const result = await request(`${DATA_API}/areas`, {
        method: "POST",
        body: JSON.stringify({
          name: cleanName,
          districtId,
        }),
      });

      setAreaName("");

      setAreaMessage({
        type: "success",
        text: result?.message || "Area added successfully.",
      });

      await loadAreas(selectedDistrictId);
    } catch (error) {
      console.error("Add area error:", error);

      setAreaMessage({
        type: "error",
        text: error?.message || "Failed to add area.",
      });
    } finally {
      setAddingArea(false);
    }
  };

  // =====================================================
  // FILTER INSTITUTES
  // =====================================================

  const filteredInstitutes = useMemo(() => {
    const search = instituteSearch.trim().toLowerCase();

    if (!search) return institutes;

    return institutes.filter((institute) =>
      String(institute?.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [institutes, instituteSearch]);

  // =====================================================
  // FILTER AREAS
  // =====================================================

  const filteredAreas = useMemo(() => {
    const search = areaSearch.trim().toLowerCase();

    if (!search) return areas;

    return areas.filter((area) =>
      String(area?.name || "")
        .toLowerCase()
        .includes(search),
    );
  }, [areas, areaSearch]);

  // =====================================================
  // SELECTED DISTRICT
  // =====================================================

  const selectedDistrict = useMemo(() => {
    return districts.find(
      (district) => String(district?.id) === String(selectedDistrictId),
    );
  }, [districts, selectedDistrictId]);

  // =====================================================
  // CHANGE MAIN TAB
  // =====================================================

  const changeTab = (tabName) => {
    setActiveTab(tabName);

    if (tabName === "institutes") {
      setAreaMessage({
        type: "",
        text: "",
      });
    } else {
      setInstituteMessage({
        type: "",
        text: "",
      });
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="tn-data-management">
      <style>{`
        .tn-data-management,
        .tn-data-management * {
          box-sizing: border-box;
        }

        .tn-data-management {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 4px 0 24px;
          color: #0f172a !important;
          font-family: inherit;
        }

        .tn-data-management h1,
        .tn-data-management h2,
        .tn-data-management h3,
        .tn-data-management p,
        .tn-data-management span,
        .tn-data-management label,
        .tn-data-management strong,
        .tn-data-management div {
          -webkit-text-fill-color: initial;
        }

        .tn-data-management .dm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 20px;
        }

        .tn-data-management .dm-title {
          margin: 0;
          color: #0f172a !important;
          font-size: 24px;
          line-height: 1.2;
          font-weight: 800;
        }

        .tn-data-management .dm-subtitle {
          margin: 6px 0 0;
          color: #64748b !important;
          font-size: 14px;
          line-height: 1.5;
        }

        .tn-data-management .dm-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e2e8f0;
        }

        .tn-data-management .dm-tab {
          appearance: none;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px;
          background: #ffffff !important;
          color: #475569 !important;
          -webkit-text-fill-color: #475569 !important;
          padding: 9px 16px;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          line-height: 1.2;
          cursor: pointer !important;
          pointer-events: auto !important;
          transition:
            background-color 0.15s ease,
            border-color 0.15s ease,
            color 0.15s ease;
        }

        .tn-data-management .dm-tab:hover {
          background: #f8fafc !important;
        }

        .tn-data-management .dm-tab.dm-tab-active {
          border-color: #0f172a !important;
          background: #0f172a !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }

        .tn-data-management .dm-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .tn-data-management .dm-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .tn-data-management .dm-section-title {
          margin: 0;
          color: #0f172a !important;
          font-size: 18px;
          font-weight: 800;
        }

        .tn-data-management .dm-section-description {
          margin: 5px 0 0;
          color: #64748b !important;
          font-size: 13px;
          line-height: 1.5;
        }

        .tn-data-management .dm-count {
          flex-shrink: 0;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          background: #f8fafc !important;
          color: #334155 !important;
          -webkit-text-fill-color: #334155 !important;
          padding: 6px 11px;
          font-size: 12px;
          font-weight: 800;
          white-space: nowrap;
        }

        .tn-data-management .dm-card {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: #ffffff !important;
          padding: 18px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
        }

        .tn-data-management .dm-label {
          display: block;
          margin: 0 0 7px;
          color: #334155 !important;
          -webkit-text-fill-color: #334155 !important;
          font-size: 12px;
          font-weight: 800;
        }

        .tn-data-management input.dm-input,
        .tn-data-management input.dm-search,
        .tn-data-management select.dm-select {
          appearance: auto;
          opacity: 1 !important;
          pointer-events: auto !important;
          user-select: text !important;
          width: 100%;
          border: 1px solid #cbd5e1 !important;
          border-radius: 10px;
          background: #ffffff !important;
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
          caret-color: #0f172a !important;
          font-family: inherit;
          font-size: 14px;
          font-weight: 600;
          line-height: normal;
          outline: none !important;
          box-shadow: none;
        }

        .tn-data-management input.dm-input {
          height: 42px;
          padding: 0 12px;
        }

        .tn-data-management input.dm-search {
          width: 280px;
          max-width: 100%;
          height: 38px;
          padding: 0 11px;
          font-size: 13px;
        }

        .tn-data-management select.dm-select {
          height: 42px;
          padding: 0 36px 0 12px;
          cursor: pointer !important;
        }

        .tn-data-management select.dm-select option {
          background: #ffffff !important;
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
        }

        .tn-data-management input.dm-input::placeholder,
        .tn-data-management input.dm-search::placeholder {
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
          opacity: 1 !important;
        }

        .tn-data-management input.dm-input:hover,
        .tn-data-management input.dm-search:hover,
        .tn-data-management select.dm-select:hover {
          border-color: #94a3b8 !important;
        }

        .tn-data-management input.dm-input:focus,
        .tn-data-management input.dm-search:focus,
        .tn-data-management select.dm-select:focus {
          border-color: #475569 !important;
          box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.12) !important;
        }

        .tn-data-management input.dm-input:disabled,
        .tn-data-management input.dm-search:disabled,
        .tn-data-management select.dm-select:disabled {
          cursor: not-allowed !important;
          background: #f8fafc !important;
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
        }

        .tn-data-management .dm-form-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .tn-data-management .dm-form-input-wrap {
          flex: 1;
          min-width: 0;
        }

        .tn-data-management .dm-grid-two {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .tn-data-management .dm-char-count {
          margin-top: 5px;
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
          font-size: 11px;
          font-weight: 600;
          text-align: right;
        }

        .tn-data-management .dm-primary-button {
          appearance: none;
          flex-shrink: 0;
          min-height: 42px;
          border: 1px solid #0f172a !important;
          border-radius: 10px;
          background: #0f172a !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          padding: 0 18px;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer !important;
          pointer-events: auto !important;
          white-space: nowrap;
          transition:
            background-color 0.15s ease,
            opacity 0.15s ease;
        }

        .tn-data-management .dm-primary-button:hover:not(:disabled) {
          background: #1e293b !important;
        }

        .tn-data-management .dm-primary-button:disabled {
          cursor: not-allowed !important;
          opacity: 0.45 !important;
        }

        .tn-data-management .dm-message {
          margin-top: 12px;
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 13px;
          font-weight: 700;
          line-height: 1.45;
        }

        .tn-data-management .dm-message-success {
          border: 1px solid #a7f3d0;
          background: #ecfdf5 !important;
          color: #047857 !important;
          -webkit-text-fill-color: #047857 !important;
        }

        .tn-data-management .dm-message-error {
          border: 1px solid #fecaca;
          background: #fef2f2 !important;
          color: #b91c1c !important;
          -webkit-text-fill-color: #b91c1c !important;
        }

        .tn-data-management .dm-selected-district {
          margin-top: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #f8fafc !important;
          padding: 10px 12px;
          color: #475569 !important;
          -webkit-text-fill-color: #475569 !important;
          font-size: 12px;
          line-height: 1.5;
        }

        .tn-data-management .dm-selected-district strong {
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
        }

        .tn-data-management .dm-button-row {
          display: flex;
          justify-content: flex-end;
          margin-top: 12px;
        }

        .tn-data-management .dm-list-card {
          width: 100%;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: #ffffff !important;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
        }

        .tn-data-management .dm-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
          padding: 15px 16px;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff !important;
        }

        .tn-data-management .dm-list-title {
          margin: 0;
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
          font-size: 15px;
          font-weight: 800;
        }

        .tn-data-management .dm-list-subtitle {
          margin: 4px 0 0;
          color: #64748b !important;
          -webkit-text-fill-color: #64748b !important;
          font-size: 12px;
          line-height: 1.5;
        }

        .tn-data-management .dm-list-scroll {
          max-height: 430px;
          overflow-y: auto;
          background: #ffffff !important;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f8fafc;
        }

        .tn-data-management .dm-list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          min-height: 48px;
          padding: 10px 16px;
          border-bottom: 1px solid #f1f5f9;
          background: #ffffff !important;
        }

        .tn-data-management .dm-list-item:last-child {
          border-bottom: none;
        }

        .tn-data-management .dm-list-item:hover {
          background: #f8fafc !important;
        }

        .tn-data-management .dm-item-main {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .tn-data-management .dm-item-id {
          flex-shrink: 0;
          min-width: 50px;
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
          font-size: 11px;
          font-weight: 700;
        }

        .tn-data-management .dm-item-name {
          overflow-wrap: anywhere;
          color: #1e293b !important;
          -webkit-text-fill-color: #1e293b !important;
          font-size: 13px;
          font-weight: 700;
        }

        .tn-data-management .dm-item-meta {
          flex-shrink: 0;
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
          font-size: 11px;
          font-weight: 600;
          text-align: right;
        }

        .tn-data-management .dm-empty {
          padding: 36px 20px;
          background: #ffffff !important;
          color: #94a3b8 !important;
          -webkit-text-fill-color: #94a3b8 !important;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.5;
          text-align: center;
        }

        .tn-data-management .dm-loading {
          padding: 36px 20px;
          background: #ffffff !important;
          color: #64748b !important;
          -webkit-text-fill-color: #64748b !important;
          font-size: 13px;
          font-weight: 700;
          text-align: center;
        }

        @media (max-width: 720px) {
          .tn-data-management {
            padding-bottom: 16px;
          }

          .tn-data-management .dm-header,
          .tn-data-management .dm-section-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .tn-data-management .dm-grid-two {
            grid-template-columns: 1fr;
          }

          .tn-data-management .dm-form-row {
            flex-direction: column;
          }

          .tn-data-management .dm-primary-button {
            width: 100%;
          }

          .tn-data-management .dm-search {
            width: 100% !important;
          }

          .tn-data-management .dm-list-header {
            align-items: stretch;
            flex-direction: column;
          }

          .tn-data-management .dm-list-item {
            align-items: flex-start;
            flex-direction: column;
          }

          .tn-data-management .dm-item-meta {
            padding-left: 60px;
            text-align: left;
          }
        }
      `}</style>

      {/* HEADER */}
      <div className="dm-header">
        <div>
          <h1 className="dm-title">Data Management</h1>

          <p className="dm-subtitle">
            Manage platform institutes and location data.
          </p>
        </div>
      </div>

      {/* TABS */}
      <div className="dm-tabs">
        <button
          type="button"
          className={`dm-tab ${
            activeTab === "institutes" ? "dm-tab-active" : ""
          }`}
          onClick={() => changeTab("institutes")}
        >
          Institutes
        </button>

        <button
          type="button"
          className={`dm-tab ${activeTab === "areas" ? "dm-tab-active" : ""}`}
          onClick={() => changeTab("areas")}
        >
          Areas
        </button>
      </div>

      {/* =====================================================
          INSTITUTES
      ===================================================== */}

      {activeTab === "institutes" && (
        <div className="dm-section">
          <div className="dm-section-header">
            <div>
              <h2 className="dm-section-title">Institute Management</h2>

              <p className="dm-section-description">
                Add institutes that tutors can select from their profile.
              </p>
            </div>

            <div className="dm-count">
              {institutes.length.toLocaleString()} Institutes
            </div>
          </div>

          {/* ADD INSTITUTE */}

          <form className="dm-card" onSubmit={handleAddInstitute}>
            <label htmlFor="dm-institute-name" className="dm-label">
              Institute Name
            </label>

            <div className="dm-form-row">
              <div className="dm-form-input-wrap">
                <input
                  id="dm-institute-name"
                  className="dm-input"
                  type="text"
                  autoComplete="off"
                  value={instituteName}
                  maxLength={150}
                  placeholder="Example: Notre Dame College"
                  onChange={(event) => {
                    setInstituteName(event.target.value);

                    if (instituteMessage.text) {
                      setInstituteMessage({
                        type: "",
                        text: "",
                      });
                    }
                  }}
                />

                <div className="dm-char-count">{instituteName.length}/150</div>
              </div>

              <button
                className="dm-primary-button"
                type="submit"
                disabled={addingInstitute || !instituteName.trim()}
              >
                {addingInstitute ? "Adding..." : "Add Institute"}
              </button>
            </div>

            {instituteMessage.text && (
              <MessageBox
                type={instituteMessage.type}
                text={instituteMessage.text}
              />
            )}
          </form>

          {/* EXISTING INSTITUTES */}

          <div className="dm-list-card">
            <div className="dm-list-header">
              <div>
                <h3 className="dm-list-title">Existing Institutes</h3>

                <p className="dm-list-subtitle">
                  Search before adding a new institute to avoid duplicates.
                </p>
              </div>

              <input
                className="dm-search"
                type="search"
                autoComplete="off"
                value={instituteSearch}
                placeholder="Search institutes..."
                onChange={(event) => setInstituteSearch(event.target.value)}
              />
            </div>

            {loadingInstitutes ? (
              <div className="dm-loading">Loading institutes...</div>
            ) : filteredInstitutes.length === 0 ? (
              <div className="dm-empty">
                {instituteSearch.trim()
                  ? "No matching institute found."
                  : "No institutes found."}
              </div>
            ) : (
              <div className="dm-list-scroll">
                {filteredInstitutes.map((institute) => (
                  <div key={institute.id} className="dm-list-item">
                    <div className="dm-item-main">
                      <span className="dm-item-id">#{institute.id}</span>

                      <span className="dm-item-name">{institute.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================
          AREAS
      ===================================================== */}

      {activeTab === "areas" && (
        <div className="dm-section">
          <div className="dm-section-header">
            <div>
              <h2 className="dm-section-title">Area Management</h2>

              <p className="dm-section-description">
                Select a district and add areas belonging to that district.
              </p>
            </div>

            {selectedDistrictId && (
              <div className="dm-count">
                {areas.length.toLocaleString()} Areas
              </div>
            )}
          </div>

          {/* ADD AREA */}

          <form className="dm-card" onSubmit={handleAddArea}>
            <div className="dm-grid-two">
              <div>
                <label htmlFor="dm-district" className="dm-label">
                  District
                </label>

                <select
                  id="dm-district"
                  className="dm-select"
                  value={selectedDistrictId}
                  disabled={loadingDistricts}
                  onChange={(event) => {
                    setSelectedDistrictId(event.target.value);

                    setAreaMessage({
                      type: "",
                      text: "",
                    });
                  }}
                >
                  <option value="">
                    {loadingDistricts
                      ? "Loading districts..."
                      : "Select District"}
                  </option>

                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                      {district.divisionName
                        ? ` — ${district.divisionName} Division`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="dm-area-name" className="dm-label">
                  Area Name
                </label>

                <input
                  id="dm-area-name"
                  className="dm-input"
                  type="text"
                  autoComplete="off"
                  value={areaName}
                  maxLength={100}
                  placeholder="Example: Bashundhara R/A"
                  onChange={(event) => {
                    setAreaName(event.target.value);

                    if (areaMessage.text) {
                      setAreaMessage({
                        type: "",
                        text: "",
                      });
                    }
                  }}
                />

                <div className="dm-char-count">{areaName.length}/100</div>
              </div>
            </div>

            {selectedDistrict && (
              <div className="dm-selected-district">
                Adding area under{" "}
                <strong>{selectedDistrict.name} District</strong>
                {selectedDistrict.divisionName
                  ? ` · ${selectedDistrict.divisionName} Division`
                  : ""}
              </div>
            )}

            <div className="dm-button-row">
              <button
                className="dm-primary-button"
                type="submit"
                disabled={addingArea || !selectedDistrictId || !areaName.trim()}
              >
                {addingArea ? "Adding..." : "Add Area"}
              </button>
            </div>

            {areaMessage.text && (
              <MessageBox type={areaMessage.type} text={areaMessage.text} />
            )}
          </form>

          {/* EXISTING AREAS */}

          <div className="dm-list-card">
            <div className="dm-list-header">
              <div>
                <h3 className="dm-list-title">
                  {selectedDistrict
                    ? `${selectedDistrict.name} Areas`
                    : "Existing Areas"}
                </h3>

                <p className="dm-list-subtitle">
                  {selectedDistrict
                    ? `Areas currently registered under ${selectedDistrict.name} District.`
                    : "Select a district to view its existing areas."}
                </p>
              </div>

              {selectedDistrictId && (
                <input
                  className="dm-search"
                  type="search"
                  autoComplete="off"
                  value={areaSearch}
                  placeholder="Search areas..."
                  onChange={(event) => setAreaSearch(event.target.value)}
                />
              )}
            </div>

            {!selectedDistrictId ? (
              <div className="dm-empty">
                Select a district above to view its areas.
              </div>
            ) : loadingAreas ? (
              <div className="dm-loading">Loading areas...</div>
            ) : filteredAreas.length === 0 ? (
              <div className="dm-empty">
                {areaSearch.trim()
                  ? "No matching area found."
                  : "No areas have been added under this district yet."}
              </div>
            ) : (
              <div className="dm-list-scroll">
                {filteredAreas.map((area) => (
                  <div key={area.id} className="dm-list-item">
                    <div className="dm-item-main">
                      <span className="dm-item-id">#{area.id}</span>

                      <span className="dm-item-name">{area.name}</span>
                    </div>

                    <div className="dm-item-meta">
                      {area.districtName || ""}
                      {area.divisionName
                        ? ` · ${area.divisionName} Division`
                        : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// MESSAGE BOX
// =====================================================

const MessageBox = ({ type, text }) => {
  const success = type === "success";

  return (
    <div
      className={`dm-message ${
        success ? "dm-message-success" : "dm-message-error"
      }`}
      role={success ? "status" : "alert"}
      aria-live="polite"
    >
      {success ? "✓" : "⚠"} {text}
    </div>
  );
};

export default DataManagement;
